import type { InstallOrigin } from "../../../domains/app/appInstallWall";
import { gaPushEvent } from "../base.analytics";

enum Event {
  INSTALL_WALL_VIEWED = "install_wall_viewed",
  INSTALL_WALL_APP_CLICKED = "install_wall_app_clicked",
  INSTALL_WALL_DISMISSED = "install_wall_dismissed",
}

/**
 * Which ask the reader met. The two are not the same offer and must never be
 * added together: `hard` has no way past it, `sheet` sits over the page the
 * reader came for and is meant to be dismissed. A single "install wall shown"
 * count mixes a wall with an invitation, and its conversion rate means nothing.
 * `inline` is part of the page itself, after the content the reader came for:
 * the offer that follows a logged-out JuisciGPT answer on mobile web.
 */
export type InstallWallKind = "hard" | "sheet" | "inline";

/**
 * How the reader got out of the sheet. `close` is the X, `continue-in-browser`
 * is the worded way out. Kept apart because they answer different questions:
 * the first says the ask was in the way, the second says the offer was read and
 * declined.
 */
export type InstallWallDismissMethod = "close" | "continue-in-browser";

/**
 * The origin, flattened for the data layer.
 *
 * `install_origin` is the discriminator and is always present, so every row can
 * be grouped by it before anyone has to know which id column is filled. The id
 * columns stay null-able and separate rather than collapsing into one
 * `install_origin_id`: an article slug and an invite code are not the same
 * namespace, and a report that joins them is a report nobody can join back.
 */
function originParams(origin: InstallOrigin): {
  install_origin: InstallOrigin["kind"];
  article_slug: string | null;
  playlist_id: string | null;
  resource_format:
    Extract<InstallOrigin, { kind: "resource" }>["format"] | null;
  resource_id: string | null;
  room_id: string | null;
  invite_code: string | null;
} {
  const base = {
    install_origin: origin.kind,
    article_slug: null,
    playlist_id: null,
    resource_format: null,
    resource_id: null,
    room_id: null,
    invite_code: null,
  };
  switch (origin.kind) {
    case "article":
      return { ...base, article_slug: origin.slug, room_id: origin.roomId };
    case "playlist":
      return {
        ...base,
        playlist_id: origin.playlistId,
        room_id: origin.roomId,
      };
    case "resource":
      return {
        ...base,
        resource_format: origin.format,
        resource_id: origin.resourceId,
        room_id: origin.roomId,
      };
    case "invite":
      return { ...base, invite_code: origin.inviteCode };
    case "front-door":
    case "gpt-answer":
    case "other":
      return base;
    default:
      throw new Error(origin satisfies never);
  }
}

/**
 * The denominator of every number below: the ask reached the screen.
 *
 * Fired through `useTrackOnMount`, keyed on the kind and the origin, so a
 * re-render, a dismissal and a reopen inside one session count one impression.
 */
export function gaEventInstallWallViewed(params: {
  kind: InstallWallKind;
  origin: InstallOrigin;
}) {
  gaPushEvent(Event.INSTALL_WALL_VIEWED, {
    install_wall_kind: params.kind,
    ...originParams(params.origin),
  });
}

/**
 * The reader took the offer: they tapped through to the store. This is a
 * STARTED install and never a finished one, which only the app can report (the
 * deferred deep link, `use-deferred-deep-link`) and which is why the two must
 * not be named alike in a report.
 */
export function gaEventInstallWallAppClicked(params: {
  kind: InstallWallKind;
  origin: InstallOrigin;
  platform: "ios" | "android" | "web";
}) {
  gaPushEvent(Event.INSTALL_WALL_APP_CLICKED, {
    install_wall_kind: params.kind,
    install_platform: params.platform,
    ...originParams(params.origin),
  });
}

/** The reader waved the sheet away and stayed on the page. */
export function gaEventInstallWallDismissed(params: {
  origin: InstallOrigin;
  method: InstallWallDismissMethod;
}) {
  gaPushEvent(Event.INSTALL_WALL_DISMISSED, {
    // Only the sheet can be dismissed, so the kind is not a parameter: a `hard`
    // dismissal would be a contradiction rather than a row.
    install_wall_kind: "sheet",
    install_dismiss_method: params.method,
    ...originParams(params.origin),
  });
}
